import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "@emotion/react";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { actions } from "@ternary/api-lib/telemetry";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import EmptyPlaceholder from "@ternary/api-lib/ui-lib/components/EmptyPlaceholder";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import { GenericFocusDataIntegrationEntity } from "@ternary/api-lib/core/types";
import { CloudProviderType } from "@ternary/api-lib/constants/enums";
import paths from "@/constants/paths";
import { useCreateGenericFocusIntegration } from "@/features/admin/hooks/useCreateGenericFocusIntegration";
import { useGetGenericFocusIntegrationByID } from "@/features/admin/integrations/hooks/useGetGenericFocusIntegrationByID";
import { useUpdateGenericFocusIntegration } from "@/features/admin/hooks/useUpdateGenericFocusIntegration";
import useAuthenticatedUser from "@/hooks/useAuthenticatedUser";
import { VerticalStepperForm } from "@/ui-lib/components/Stepper";
import { AlertType, postAlert } from "@/utils/alerts";
import { useActivityTracker } from "@/context/ActivityTrackerProvider";
import { useNavigateWithSearchParams } from "@/lib/react-router";
import queryKeys from "../../../hooks/queryKeys";
import copyText from "../../copyText";
import {
  FullyQualifiedResourceType,
  parseFullyQualifiedResourceString,
} from "../FullyQualifiedResourceString";
import { GenericFocusIntegrationFormGeneral } from "./GenericFocusIntegrationFormGeneral";
import { FormData } from "./types";
import { DEFAULT_VALUES } from "./constants";

export function GenericFocusIntegrationFormContainer() {
  const activityTracker = useActivityTracker();
  const authenticatedUser = useAuthenticatedUser();
  const navigate = useNavigateWithSearchParams();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { integrationID } = useParams();
  const [referenceType, setReferenceType] = useState<
    FullyQualifiedResourceType | undefined
  >();

  //
  // Queries
  //

  const {
    data: genericFocusIntegration,
    isLoading: isLoadingGenericFocusIntegration,
  } = useGetGenericFocusIntegrationByID(integrationID ?? "", {
    enabled: !!integrationID,
  });

  try {
    const { type } = parseFullyQualifiedResourceString(
      genericFocusIntegration?.config.storageUri ?? ""
    );

    if (type !== referenceType) {
      setReferenceType(type);
    }
  } catch {
    // Do nothing. Reference type already initialized.
  }

  //
  // Mutations
  //

  const {
    isPending: isCreatingGenericFocusIntegration,
    mutate: createGenericFocusIntegration,
  } = useCreateGenericFocusIntegration({
    onError: () => {
      postAlert({
        message: copyText.errorCreatingDataIntegrationMessage,
        type: AlertType.ERROR,
      });
    },
    onSuccess: () => {
      // TODO: Validate focus billing integration once https://gitlab.com/ternary-app/backend/-/merge_requests/2827 is merged

      postAlert({
        message: copyText.successCreatingDataIntegrationMessage,
        type: AlertType.SUCCESS,
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.dataIntegrations, authenticatedUser.tenant.id],
      });
      navigate(paths._admin, { searchParams: { tab: "integrations" } });
    },
  });

  const {
    isPending: isUpdatingGenericFocusIntegration,
    mutate: updateGenericFocusIntegration,
  } = useUpdateGenericFocusIntegration({
    onError: () => {
      postAlert({
        message: copyText.errorUpdatingDataIntegrationMessage,
        type: AlertType.ERROR,
      });
    },
    onSuccess: () => {
      // TODO: Validate focus billing integration once https://gitlab.com/ternary-app/backend/-/merge_requests/2827 is merged
      postAlert({
        message: copyText.successUpdatingDataIntegrationMessage,
        type: AlertType.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.dataIntegrations, authenticatedUser.tenant.id],
      });
      navigate(paths._admin, { searchParams: { tab: "integrations" } });
    },
  });

  //
  // Interaction Handlers
  //

  const defaultValues = genericFocusIntegration
    ? getValuesFromIntegration(genericFocusIntegration)
    : DEFAULT_VALUES;

  function handleCreateIntegration(tenantID: string, value: FormData) {
    activityTracker.captureAction(actions.CLICK_ADMIN_CREATE_INTEGRATION, {
      type: CloudProviderType.GENERIC_FOCUS,
    });

    createGenericFocusIntegration({
      tenantID,
      ...value,
    });
  }

  function handleUpdateIntegration(integrationID: string, values: FormData) {
    const changes = getChanges(values, defaultValues);

    activityTracker.captureAction(actions.CLICK_ADMIN_UPDATE_INTEGRATION, {
      type: CloudProviderType.GENERIC_FOCUS,
    });

    updateGenericFocusIntegration({
      integrationID,
      ...changes,
    });
  }

  async function handleSubmit({ value }: { value: FormData }) {
    const formValues = {
      ...value,
      storageUri: sanitizeStorageUri(value.storageUri),
    };

    if (genericFocusIntegration) {
      if (!defaultValues) return;

      handleUpdateIntegration(genericFocusIntegration.id, formValues);
    } else {
      handleCreateIntegration(authenticatedUser.tenant.id, formValues);
    }
  }

  const steps = [
    {
      component: (form) => (
        <GenericFocusIntegrationFormGeneral
          form={form}
          referenceType={referenceType}
          setReferenceType={setReferenceType}
        />
      ),
      label: copyText.genericFocusFormGeneralStepLabel,
      requiredInputs:
        referenceType === "S3Ref"
          ? ["name", "storageUri", "roleArn"]
          : ["name", "storageUri"],
    },
    {
      component: () => (
        <Box width="50%">
          <Text appearance="h4">{copyText.createIntegrationHeader}</Text>
          <Text marginBottom={theme.space_lg}>
            {copyText.integrationLastInstructions}
          </Text>
        </Box>
      ),
      label: copyText.submitButtonLabel,
    },
  ];

  return (
    <Flex direction="column" height="80vh" gap={theme.space_md}>
      <Button
        iconStart={<Icon icon={faArrowLeft} />}
        secondary
        width={200}
        onClick={() =>
          navigate(paths._admin, { searchParams: { tab: "integrations" } })
        }
      >
        {copyText.backToIntegrationsButtonLabel}
      </Button>
      <Text appearance="h3">
        {genericFocusIntegration
          ? genericFocusIntegration.name
          : copyText.genericFocusFormNewIntegrationHeader}
      </Text>
      <Flex height="100%" width="100%">
        {isLoadingGenericFocusIntegration ? (
          <EmptyPlaceholder loading width="50%" />
        ) : (
          <VerticalStepperForm
            defaultValues={defaultValues}
            isProcessing={
              isCreatingGenericFocusIntegration ||
              isUpdatingGenericFocusIntegration
            }
            steps={steps}
            onSubmit={handleSubmit}
          />
        )}
      </Flex>
    </Flex>
  );
}

function getValuesFromIntegration(
  integration: GenericFocusDataIntegrationEntity
) {
  return {
    name: integration.name ?? "",
    roleArn: integration.config.roleArn,
    storageUri: integration.config.storageUri ?? "",
  };
}

function getChanges(value: FormData, defaultValues: FormData) {
  const changes: Partial<FormData> = {};

  for (const key in value) {
    if (value[key] !== defaultValues[key]) {
      changes[key] = value[key].trim();
    }
  }

  return changes;
}

function sanitizeStorageUri(uri: string): string {
  // If it already ends with a slash, return as is
  if (uri.endsWith("/")) {
    return uri;
  }

  // Check if the end of the URI contains a file extension
  const hasFileExtension = uri.split("/").pop()?.includes(".");

  return hasFileExtension ? uri.substring(0, uri.lastIndexOf("/") + 1) : uri;
}
